import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { fetchSubscriptionSummary, SubscriptionSummary } from '../api/subscription';
import { useAuth } from './authContext';
import { AuthError } from '../utils/apiClient';
import { formatApiError } from '../utils/formatApiError';

type SubscriptionContextType = {
  subscription: SubscriptionSummary | null;
  loading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { authData } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSubscription = async () => {
    if (!authData?.organizationId) {
      setSubscription(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchSubscriptionSummary();
      setSubscription(data);
    } catch (err: any) {
      if (err instanceof AuthError) {
        return;
      }

      setError(formatApiError(err, 'Failed to load subscription'));
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadSubscription();
  }, [authData?.organizationId]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        error,
        refreshSubscription: loadSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }

  return context;
};
